// ==============================================
// Admin Packages Page
// ==============================================

import { PackageEditor } from '../../components/admin/packageEditor/PackageEditor';
import { PlanCompareGrid, type PlanSaveItem, type PlanSaveResult } from '../../components/admin/packageEditor/PlanCompareGrid';
import { emptyPackageForm, packageToForm, type PackageFormData } from '../../components/admin/packageEditor/packageForm';
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  ArchiveBoxIcon,
  ArrowPathIcon,
  TrashIcon,
  XMarkIcon,
  UsersIcon,
  DocumentDuplicateIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  StarIcon as StarOutlineIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { api } from '../../services/api';
import type { Package } from '../../types/subscription';

function StandardFeaturesEditor() {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(true);
  const [featureInput, setFeatureInput] = useState('');
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [editVal, setEditVal] = useState('');

  const { data: features = [] } = useQuery<string[]>({
    queryKey: ['admin', 'standard-features'],
    queryFn: () => api.adminGetStandardFeatures(),
  });

  const mutation = useMutation({
    mutationFn: (updated: string[]) => api.adminUpdateStandardFeatures(updated),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'standard-features'] });
      queryClient.invalidateQueries({ queryKey: ['standard-features'] });
    },
  });

  const addFeature = () => {
    const trimmed = featureInput.trim();
    if (!trimmed || features.includes(trimmed)) return;
    mutation.mutate([...features, trimmed]);
    setFeatureInput('');
  };

  const removeFeature = (idx: number) => {
    mutation.mutate(features.filter((_, i) => i !== idx));
  };

  const saveEdit = () => {
    if (editIdx === null) return;
    const trimmed = editVal.trim();
    if (trimmed) {
      const updated = [...features];
      updated[editIdx] = trimmed;
      mutation.mutate(updated);
    }
    setEditIdx(null);
    setEditVal('');
  };

  // Drag reorder
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const handleDrop = (dropIdx: number) => {
    if (dragIdx === null || dragIdx === dropIdx) {
      setDragIdx(null); setDragOverIdx(null); return;
    }
    const reordered = [...features];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    mutation.mutate(reordered);
    setDragIdx(null); setDragOverIdx(null);
  };

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-slate-700/30 transition-colors"
      >
        <div className="flex items-center gap-3">
          <CheckCircleIcon className="w-5 h-5 text-green-500" />
          <div className="text-left">
            <h3 className="text-sm font-semibold text-white">Standard Product Features</h3>
            <p className="text-xs text-slate-400">Displayed on the home page above pricing plans — "All plans include"</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">{features.length} features</span>
          {isOpen ? <ChevronUpIcon className="w-4 h-4 text-slate-400" /> : <ChevronDownIcon className="w-4 h-4 text-slate-400" />}
        </div>
      </button>

      {isOpen && (
        <div className="px-6 pb-5 border-t border-slate-700">
          <div className="flex gap-2 mt-4 mb-3">
            <input
              type="text"
              value={featureInput}
              onChange={(e) => setFeatureInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addFeature(); } }}
              className="flex-1 px-3 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white text-sm focus:border-cyan-500 focus:outline-none"
              placeholder="Add a standard feature..."
            />
            <button
              type="button"
              onClick={addFeature}
              className="px-3 py-2 bg-cyan-500 text-white rounded-lg hover:bg-cyan-400"
            >
              <PlusIcon className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-1">
            {features.map((feature, idx) => (
              editIdx === idx ? (
                <div key={idx} className="flex gap-2">
                  <input
                    type="text"
                    value={editVal}
                    onChange={(e) => setEditVal(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); saveEdit(); }
                      if (e.key === 'Escape') { setEditIdx(null); setEditVal(''); }
                    }}
                    onBlur={saveEdit}
                    autoFocus
                    className="flex-1 px-3 py-1.5 bg-slate-900 border border-cyan-500 rounded text-sm text-white focus:outline-none"
                  />
                </div>
              ) : (
                <div
                  key={idx}
                  draggable
                  onDragStart={() => setDragIdx(idx)}
                  onDragOver={(e) => { e.preventDefault(); setDragOverIdx(idx); }}
                  onDrop={() => handleDrop(idx)}
                  onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                  onDoubleClick={() => { setEditIdx(idx); setEditVal(feature); }}
                  className={`flex items-center gap-3 px-3 py-1.5 rounded text-sm cursor-grab active:cursor-grabbing select-none transition-all group ${
                    dragIdx === idx
                      ? 'opacity-40 bg-slate-700'
                      : dragOverIdx === idx
                        ? 'bg-cyan-500/20 ring-1 ring-cyan-500'
                        : 'bg-slate-700/50 hover:bg-slate-700'
                  }`}
                >
                  <span className="text-slate-500 text-xs font-mono w-4">{idx + 1}</span>
                  <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span className="text-slate-200 flex-1">{feature}</span>
                  <button
                    onClick={() => removeFeature(idx)}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-red-400 transition-opacity"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )
            ))}
          </div>
          {features.length > 0 && (
            <p className="text-xs text-slate-500 mt-2">Drag to reorder • Double-click to edit</p>
          )}
        </div>
      )}
    </div>
  );
}

export function AdminPackagesPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [editingPackage, setEditingPackage] = useState<Package | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [cloneSource, setCloneSource] = useState<PackageFormData | null>(null);
  const [view, setView] = useState<'list' | 'compare'>('list');

  const { data: packages, isLoading } = useQuery<Package[]>({
    queryKey: ['admin', 'packages'],
    queryFn: () => api.adminGetPackages(),
  });

  const { data: defaultPackage } = useQuery<Package | null>({
    queryKey: ['admin', 'packages', 'default'],
    queryFn: () => api.adminGetDefaultPackage(),
  });

  // For the editor's review step and History panel; only while a saved plan is open.
  const { data: subscriberCounts } = useQuery({
    queryKey: ['admin', 'packages', 'subscriber-counts'],
    queryFn: () => api.adminGetPackageSubscriberCounts(),
    enabled: !!editingPackage || view === 'compare',
  });
  const { data: history } = useQuery({
    queryKey: ['admin', 'packages', editingPackage?.id, 'changes'],
    queryFn: () => api.adminGetPackageChanges(editingPackage!.id),
    enabled: !!editingPackage,
  });

  const allFeatures = useMemo(() => {
    if (!packages) return [];
    const set = new Set<string>();
    packages.forEach((p) => p.features?.forEach((f) => set.add(f)));
    return Array.from(set).sort();
  }, [packages]);

  /** Compare plans saves each changed plan in turn, so one failure never stops the rest. */
  const saveFromGrid = async (items: PlanSaveItem[]): Promise<PlanSaveResult[]> => {
    const results: PlanSaveResult[] = [];
    for (const item of items) {
      try {
        await api.adminUpdatePackage(item.id, item.data, item.reason);
        results.push({ id: item.id, ok: true });
      } catch (err: any) {
        results.push({ id: item.id, ok: false, error: err?.response?.data?.error || err?.message || 'Save failed' });
      }
    }
    queryClient.invalidateQueries({ queryKey: ['admin', 'packages'] });
    return results;
  };

  const createMutation = useMutation({
    mutationFn: (data: Partial<Package>) => api.adminCreatePackage(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'packages'] });
      setIsCreating(false);
      setCloneSource(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data, reason }: { id: string; data: Partial<Package>; reason?: string }) => api.adminUpdatePackage(id, data, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'packages'] });
      setEditingPackage(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.adminDeletePackage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'packages'] });
    },
    onError: (error: any) => {
      const message = error?.response?.data?.error || error?.message || 'Failed to delete package';
      alert(message);
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: (id: string) => api.adminSetDefaultPackage(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'packages'] });
    },
  });

  const handleSetDefault = (pkg: Package) => {
    const currentName = defaultPackage?.name;
    const message = currentName
      ? `"${currentName}" is currently the default package for new registrations.\n\nChanging this will make "${pkg.name}" the new default. All future sign-ups will receive "${pkg.name}" instead.\n\nExisting users will not be affected.\n\nContinue?`
      : `Set "${pkg.name}" as the default package for all new registrations?\n\nAll future sign-ups will automatically receive this package.`;

    if (confirm(message)) {
      setDefaultMutation.mutate(pkg.id);
    }
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD',
    }).format(cents / 100);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isCreating || editingPackage) {
    const initialData: PackageFormData = editingPackage
      ? packageToForm(editingPackage)
      : cloneSource || emptyPackageForm;

    return (
      <div className="space-y-6">
        <PackageEditor
          title={editingPackage ? 'Edit Package' : cloneSource ? 'Clone Package' : 'Create Package'}
          pkg={initialData}
          isNew={!editingPackage}
          subscriberCount={editingPackage ? subscriberCounts?.[editingPackage.id] ?? null : null}
          history={editingPackage ? history ?? [] : undefined}
          onSave={(data, reason) => {
            if (editingPackage) {
              updateMutation.mutate({ id: editingPackage.id, data, reason });
            } else {
              createMutation.mutate(data);
            }
          }}
          onCancel={() => {
            setIsCreating(false);
            setEditingPackage(null);
            setCloneSource(null);
          }}
          isLoading={createMutation.isPending || updateMutation.isPending}
          allFeatures={allFeatures}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white">Packages</h1>
          <p className="text-slate-400">Manage subscription packages</p>
        </div>
        <div role="tablist" aria-label="View" className="ml-auto mr-3 flex gap-1 rounded-lg border border-slate-700 bg-slate-900/50 p-1">
          {([['list', 'List'], ['compare', 'Compare plans']] as const).map(([v, label]) => (
            <button key={v} role="tab" aria-selected={view === v} onClick={() => setView(v)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium ${view === v ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`}>
              {label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setIsCreating(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-400 hover:to-blue-500"
        >
          <PlusIcon className="w-5 h-5" />
          Create Package
        </button>
      </div>

      {view === 'compare' ? (
        <PlanCompareGrid packages={packages ?? []} subscriberCounts={subscriberCounts}
          saveChanges={saveFromGrid} onOpen={(pkg) => setEditingPackage(pkg)} />
      ) : (
      <>
      <StandardFeaturesEditor />

      <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-sm text-slate-400 border-b border-slate-700 bg-slate-800/50">
              <th className="px-6 py-3 font-medium">Package</th>
              <th className="px-6 py-3 font-medium">Price</th>
              <th className="px-6 py-3 font-medium">Tokens</th>
              <th className="px-6 py-3 font-medium">Status</th>
              <th className="px-6 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50">
            {packages?.map((pkg) => (
              <tr key={pkg.id} className="hover:bg-slate-800/30">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="font-medium text-white">{pkg.name}</p>
                      <p className="text-xs text-slate-400">{pkg.slug}</p>
                    </div>
                    {defaultPackage?.id === pkg.id && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-amber-500/20 text-amber-400 rounded">
                        <StarSolidIcon className="w-3 h-3" />
                        Default
                      </span>
                    )}
                    {pkg.is_popular && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-cyan-500/20 text-cyan-400 rounded">
                        Popular
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <p className="text-white">{formatCurrency(pkg.monthly_price_cents)}/mo</p>
                  <p className="text-xs text-slate-400">NZ${((pkg.monthly_price_cents / 100) * 3).toFixed(2)}/qtr</p>
                </td>
                <td className="px-6 py-4">
                  <p className="text-white">{pkg.included_tokens.toLocaleString()}</p>
                  <p className="text-xs text-slate-400">
                    {formatCurrency(pkg.cost_per_1000_tokens_cents)}/1k overage
                  </p>
                </td>
                <td className="px-6 py-4">
                  <span
                    className={`px-2 py-1 text-xs font-medium rounded ${
                      pkg.visibility === 'active'
                        ? 'bg-green-500/20 text-green-400'
                        : pkg.visibility === 'hidden'
                        ? 'bg-yellow-500/20 text-yellow-400'
                        : 'bg-slate-500/20 text-slate-400'
                    }`}
                  >
                    {pkg.visibility}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleSetDefault(pkg)}
                      className={`p-1.5 rounded hover:bg-slate-700 ${
                        defaultPackage?.id === pkg.id
                          ? 'text-amber-400'
                          : 'text-slate-400 hover:text-amber-400'
                      }`}
                      title={defaultPackage?.id === pkg.id ? 'Current default package' : 'Set as default package'}
                      disabled={setDefaultMutation.isPending}
                    >
                      {defaultPackage?.id === pkg.id ? (
                        <StarSolidIcon className="w-4 h-4" />
                      ) : (
                        <StarOutlineIcon className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => setEditingPackage(pkg)}
                      className="p-1.5 text-slate-400 hover:text-white rounded hover:bg-slate-700"
                      title="Edit"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => navigate(`/admin/users?package=${pkg.id}`)}
                      className="p-1.5 text-slate-400 hover:text-cyan-400 rounded hover:bg-slate-700"
                      title="View Subscribers"
                    >
                      <UsersIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setCloneSource({
                          ...packageToForm(pkg),
                          name: `${pkg.name} (Copy)`,
                          slug: `${pkg.slug}-copy`,
                          features: [...(pkg.features || [])],
                          visibility: 'active',
                          sort_order: (pkg.sort_order || 0) + 1,
                          is_popular: false,
                        });
                        setIsCreating(true);
                      }}
                      className="p-1.5 text-slate-400 hover:text-purple-400 rounded hover:bg-slate-700"
                      title="Clone Package"
                    >
                      <DocumentDuplicateIcon className="w-4 h-4" />
                    </button>
                    {pkg.visibility === 'archived' ? (
                      <button
                        onClick={() => updateMutation.mutate({ id: pkg.id, data: { visibility: 'active' } })}
                        className="p-1.5 text-slate-400 hover:text-green-400 rounded hover:bg-slate-700"
                        title="Restore"
                      >
                        <ArrowPathIcon className="w-4 h-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => updateMutation.mutate({ id: pkg.id, data: { visibility: 'archived' } })}
                        className="p-1.5 text-slate-400 hover:text-yellow-400 rounded hover:bg-slate-700"
                        title="Archive"
                      >
                        <ArchiveBoxIcon className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this package? This cannot be undone.')) {
                          deleteMutation.mutate(pkg.id);
                        }
                      }}
                      className="p-1.5 text-slate-400 hover:text-red-400 rounded hover:bg-slate-700"
                      title="Delete"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      </>
      )}
    </div>
  );
}

export default AdminPackagesPage;
